import React, { useEffect, useRef, useState } from 'react';
import MemberFamilyDialog from './MemberFamilyDialog';
import type { DashboardFamilyMember } from './types';
import { previewFamilyChange, submitFamilyChangeRequest } from './familyChangeClient';

interface RequestPortalAccessDialogProps {
  /** The household member being asked for a login (no cognitoSub). */
  member: DashboardFamilyMember;
  onClose: () => void;
  onDone: (message: string) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Ask for a login" (#150 slice 2, R6, design §6): the household primary
 * requests a portal account for a family member who doesn't have one yet.
 * Previews first (no write), then submits — same two-step pattern as
 * `AddFamilyMemberDialog`. Nothing changes until an admin approves it.
 */
const RequestPortalAccessDialog: React.FC<RequestPortalAccessDialogProps> = ({
  member,
  onClose,
  onDone,
}) => {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [previewed, setPreviewed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const memberName = `${member.firstName} ${member.lastName}`;
  const isJunior = member.isJunior === true || member.familyRole === 'CHILD';

  const validate = (): boolean => {
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handlePreview = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await previewFamilyChange('PORTAL_ACCESS', {
        memberId: member.id,
        email: email.trim(),
      });
      if (result.success) {
        setPreviewed(true);
        setPreviewMessage(result.message || null);
      } else {
        setServerError(result.message || 'Could not check this request. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not check this request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await submitFamilyChangeRequest('PORTAL_ACCESS', {
        memberId: member.id,
        email: email.trim(),
      });
      if (result.success) {
        onDone(result.message || `Login requested for ${memberName}. Waiting for approval.`);
      } else {
        setServerError(result.message || 'Could not submit this request. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not submit this request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MemberFamilyDialog
      titleId="rpa-title"
      title={`Ask for a login for ${memberName}?`}
      descriptionId="rpa-desc"
      busy={submitting}
      onClose={onClose}
    >
      <div id="rpa-desc" className="mt-2 space-y-2 text-sm text-gray-700">
        <p>
          An admin will check and approve this. {member.firstName} will then get an email with their
          sign-in details.
        </p>
        {isJunior && (
          <p className="rounded-md border border-blue-200 bg-blue-50 p-2 text-blue-900">
            The login will be theirs. The email should be one {member.firstName} uses.
          </p>
        )}
      </div>

      <div className="mt-3">
        <label htmlFor="rpa-email" className="block text-sm font-medium text-gray-700 mb-1">
          {member.firstName}&rsquo;s email *
        </label>
        <input
          ref={emailInputRef}
          id="rpa-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setPreviewed(false);
            setPreviewMessage(null);
          }}
          className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
        />
        {emailError && (
          <p role="alert" className="mt-1 text-sm font-medium text-red-700">
            {emailError}
          </p>
        )}
      </div>

      {previewMessage && (
        <p className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">
          {previewMessage}
        </p>
      )}

      <div aria-live="polite">
        {serverError && (
          <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
            {serverError}
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        {!previewed ? (
          <button
            type="button"
            onClick={handlePreview}
            disabled={submitting || !email.trim()}
            className="min-h-[44px] rounded-lg border border-fishing-blue px-4 py-2 font-semibold text-fishing-blue hover:bg-blue-50 disabled:opacity-50"
          >
            {submitting ? 'Checking…' : 'Preview'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={submitting}
            className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Send'}
          </button>
        )}
      </div>
    </MemberFamilyDialog>
  );
};

export default RequestPortalAccessDialog;
